import React, { useEffect, useState } from 'react';
import { CheckCircle2, Star, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../component/Header';
import Footer from '../component/Footer';
import { getPlans, Plan } from '../services/planService';

const normalizePlanName = (name: string) =>
  name.trim().toLowerCase().replace(/\s+monthly$/, '').replace(/^pro$/, 'premium');

const firstLiveClassFeature = 'Access to scheduled instructor-led live classes';

const sortPlansWithPremiumInMiddle = (plans: Plan[]) => {
  const premium = plans.find((plan) => normalizePlanName(plan.name) === 'premium');
  const otherPlans = plans
    .filter((plan) => plan !== premium)
    .sort((left, right) => Number(left.price) - Number(right.price));

  if (!premium) return otherPlans;

  otherPlans.splice(Math.ceil(otherPlans.length / 2), 0, premium);
  return otherPlans;
};

const formatPrice = (plan: Plan) =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: plan.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(plan.price));

const Pricing = () => {
  const navigate = useNavigate();
  const ctaText = 'BUY NOW';
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState<string | null>(null);

  const loadPlans = async () => {
    setIsLoadingPlans(true);
    setPlansError(null);
    try {
      const response = await getPlans();
      const activePlans = (response.data || []).filter((plan) => plan.is_active);

      if (activePlans.length > 0) {
        setPlans(sortPlansWithPremiumInMiddle(activePlans));
      } else {
        setPlans([]);
        setPlansError('No active subscription plans available at this time.');
      }
    } catch (err: any) {
      setPlans([]);
      setPlansError(err?.message || 'Failed to load subscription plans. Please try again.');
    } finally {
      setIsLoadingPlans(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  return (
    <>
      <Header />

      <main className="font-sans text-zinc-900 ">
        <section className="relative overflow-hidden bg-white pt-24 md:pt-32 pb-24">
          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage: 'linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)',
              backgroundSize: '40px 40px',
            }}
          ></div>

          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white pointer-events-none"></div>

          <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-12 text-center mb-16 md:mb-24">
            <h1 className="mb-6 text-5xl font-black tracking-tighter text-zinc-950 md:text-6xl">
              Flexible Plans for Practical AI Learning
            </h1>

            <p className="mx-auto max-w-2xl text-lg font-medium text-zinc-600 leading-relaxed">
              Choose a plan that fits your learning goals, with access to practical AI courses, useful resources and instructor-led learning opportunities.
            </p>
          </div>

          <div className="relative z-10 mx-auto grid max-w-7xl gap-8 px-6 lg:grid-cols-3 lg:px-12 items-center mb-32">
            {isLoadingPlans && (
              <div className="lg:col-span-3 flex flex-col items-center justify-center py-20 text-center">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-amber-500 border-t-transparent mb-4"></div>
                <p className="text-zinc-600 font-medium">Loading pricing plans...</p>
              </div>
            )}
            {!isLoadingPlans && plansError && (
              <div className="lg:col-span-3 flex flex-col items-center justify-center py-12 px-6 rounded-2xl border border-red-200 bg-red-50/50 text-center">
                <p className="font-semibold text-red-600 mb-4">{plansError}</p>
                <button
                  type="button"
                  onClick={loadPlans}
                  className="rounded-xl bg-zinc-950 px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-white hover:bg-zinc-800 transition-colors"
                >
                  Retry
                </button>
              </div>
            )}
            {!isLoadingPlans && !plansError && plans.map((plan) => {
              const isHighlighted = normalizePlanName(plan.name) === 'premium';
              const cardClassName = isHighlighted
                ? 'relative rounded-[2rem] border-2 border-[#f59e0b] bg-zinc-950 p-8 md:p-12 shadow-[0_20px_50px_rgba(245,158,11,0.2)] transform md:-translate-y-4 z-10 flex flex-col h-full text-center'
                : 'rounded-[2rem] border border-zinc-200 bg-white p-8 md:p-10 shadow-xl transition-transform hover:-translate-y-1 duration-300 flex flex-col h-full text-center';
              const featureIconClassName = isHighlighted
                ? 'h-5 w-5 text-[#f59e0b] shrink-0'
                : 'h-5 w-5 text-zinc-900 shrink-0';
              const buttonClassName = isHighlighted
                ? 'block min-h-12 rounded-xl bg-[#f59e0b] px-5 py-4 text-center text-sm font-black uppercase tracking-widest text-zinc-950 transition-all shadow-[0_0_20px_rgba(163,230,53,0.3)] hover:-translate-y-1 mt-auto disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0'
                : 'hover:bg-white hover:text-zinc-950 hover:border-[#09090B] border border-[#09090B] block min-h-12 rounded-xl bg-[#09090B] text-white px-5 py-4 text-center text-sm font-black uppercase tracking-widest transition-all shadow-md hover:shadow-lg mt-auto disabled:cursor-not-allowed disabled:opacity-60';

              return (
                <article key={plan.id} className={cardClassName}>
                  {isHighlighted && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-[#f59e0b] px-4 py-1.5 text-xs font-black uppercase tracking-widest text-zinc-950 shadow-md flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 fill-zinc-950" /> Most Popular
                    </div>
                  )}

                  <h3 className={`${isHighlighted ? 'text-2xl text-[#f59e0b]' : 'text-xl text-zinc-950'} font-black mb-6`}>
                    {plan.name}
                  </h3>

                  <h2 className={`${isHighlighted ? 'mb-8 text-6xl text-white border-zinc-800' : 'mb-8 text-5xl text-zinc-950 border-zinc-100'} font-black flex items-end justify-center gap-1 border-b pb-8`}>
                    {formatPrice(plan)}
                    <span className={`${isHighlighted ? 'font-medium text-zinc-400' : 'font-bold text-zinc-500'} text-sm mb-2 tracking-normal`}>
                      /Per Month
                    </span>
                  </h2>

                  <ul className={`${isHighlighted ? 'text-white' : 'text-zinc-600'} mb-10 space-y-5 text-sm font-bold flex-1 text-left`}>
                    {(plan.features || []).map((feature) => (
                      <React.Fragment key={feature.name}>
                        {isHighlighted && feature.name === firstLiveClassFeature && (
                          <li className="pt-3 text-xs font-black uppercase tracking-widest text-[#f59e0b]" aria-label="Includes Live Classes">
                            Includes Live Classes
                          </li>
                        )}
                        <li className={`flex items-center gap-3 ${feature.is_included ? '' : 'opacity-50'}`}>
                          {feature.is_included ? (
                            <CheckCircle2 className={featureIconClassName} />
                          ) : (
                            <XCircle className="h-5 w-5 shrink-0 text-zinc-400" />
                          )}
                          <span className={feature.is_included ? '' : 'line-through'}>{feature.name}</span>
                        </li>
                      </React.Fragment>
                    ))}
                  </ul>

                  <button
                    type="button"
                    disabled={isLoadingPlans}
                    className={buttonClassName}
                    onClick={() =>
                      navigate('/checkout/', {
                        state: {
                          plan_id: plan.id,
                          plan_key: normalizePlanName(plan.name),
                          plan_name: plan.name,
                          price: Number(plan.price),
                        },
                      })
                    }
                  >
                    {isLoadingPlans ? 'LOADING...' : ctaText}
                  </button>
                </article>
              );
            })}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Pricing;
